<template>
  <div class="brochure-index">
    <AppNavigation />

    <!-- Hero Section -->
    <header class="hero-section overflow-hidden" id="hero">
      <div class="container h-100">
        <div class="row h-100 align-items-center">
          <div class="col-lg-9" data-aos="fade-up" data-aos-duration="1000">
            <h5 class="eyebrow-text uppercase mb-3">Sankoree Institute of Global Negotiators</h5>
            <h1 class="display-3 font-weight-bold mb-4">SANKOREE <span class="text-accent underline-effect">INSTITUTE</span><br>Dealmakers 4 Africa</h1>
            <!-- <p class="lead mb-2 text-gray font-weight-bold uppercase tracking-wider">CO-HOSTING PROPOSAL</p> -->
            <p class="lead mb-5 text-gray outfit">In line with the Accra Reset Vision | In support of the SIGN Mandate</p>
            <div class="cta-group d-flex flex-column flex-md-row">
                <router-link to="/SIGN/DC/2026" class="btn btn-primary btn-lg px-5 py-3 mb-3 mb-md-0 mr-md-4 shadow-sm w-100-mobile">REGISTER NOW</router-link>
                <a href="#events" class="btn btn-outline-dark btn-lg px-5 py-3 shadow-sm w-100-mobile">View Schedule</a>
            </div>
          </div>
        </div>
      </div>
      <div class="scroll-indicator d-none d-md-flex">
        <span>SCROLL TO DISCOVER</span>
        <div class="line"></div>
      </div>
    </header>

    <!-- Events at a Glance Section -->
    <section id="events" class="glance-section py-large bg-light overflow-hidden">
        <div class="container">
            <div class="row mb-5 justify-content-center text-center">
                <div class="col-lg-8" data-aos="fade-up">
                    <h6 class="text-accent font-weight-bold mb-3 tracking-widest uppercase">Summary</h6>
                    <h2 class="section-title mb-4">Events at a Glance</h2>
                    <p class="lora text-muted">A summary of the upcoming inaugural sessions and high-level engagements.</p>
                </div>
            </div>
            <div class="row justify-content-center">
                <div class="col-lg-10">
                    <div class="event-table-wrapper shadow-lg rounded-xl overflow-hidden bg-white" data-aos="zoom-in">
                        <div class="table-responsive">
                            <table class="table table-hover mb-0">
                                <thead class="bg-dark text-white outfit">
                                    <tr>
                                        <th class="py-4 px-4">Event</th>
                                        <th class="py-4 px-4">Details</th>
                                    </tr>
                                </thead>
                                <tbody class="lora">
                                    <tr>
                                        <td class="py-4 px-4 font-weight-bold align-middle">Inaugural Sankoree Lectures</td>
                                        <td class="py-4 px-4">
                                            <strong>Bringing Sovereignty into Practice in Africa</strong><br>
                                            <span class="text-muted d-block mt-1">Virtual | 15 April 2026 | 2:00 PM - 5:30 PM EST</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="py-4 px-4 font-weight-bold align-middle">Dealmakers 4 Africa Dinner</td>
                                        <td class="py-4 px-4">
                                            <strong>Hosted by President Olusegun Obasanjo</strong><br>
                                            <span class="text-muted d-block mt-1">
                                                Washington DC | 16th April 2026<br>
                                                Pre-event Briefing & Cocktail: 5:30 PM | Dinner: 7:30 - 10:00 PM EST<br>
                                                <small class="font-italic text-accent">Capacity: 100 Guests</small>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- About Section: The Background -->
    <section id="about" class="about-section py-large overflow-hidden">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-lg-6 mb-5 mb-lg-0" data-aos="fade-right">
                    <h6 class="text-accent font-weight-bold mb-3 tracking-widest uppercase">The Background</h6>
                    <h2 class="section-title mb-4">Bridging the Gap in Leadership</h2>
                    <div class="brochure-text lora">
                        <p>There is a serious gap in the curriculum of African leadership formation that most institutions have been much too reticent to confront for many decades. Leaders and negotiators emerging from the continent's most sophisticated governance and policy schools leave with analytical frameworks drawn overwhelmingly from the intellectual traditions of the north Atlantic.</p>
                        <p>They can explain comparative advantage. They can model debt sustainability. They can cite Rawls and Habermas. What they cannot easily do, and what no formally constituted discipline yet trains them to do, is read the architecture of the international system from the vantage point of a state that did not author it, and negotiate within that system from a position of studied, strategic, and historically informed agency.</p>
                    </div>
                </div>
                <div class="col-lg-6" data-aos="fade-left">
                    <div class="accent-card p-5 rounded-lg border-accent">
                        <p class="lora italic mb-0">"The true significance of leadership is the least appreciated: Negotiation. It is the primary medium through which cumulative capacity is exercised or forfeited."</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- The Institute Section: SIGN Mandate -->
    <section id="theInstitute" class="mandate-section py-large bg-dark text-white overflow-hidden">
        <div class="container">
            <div class="row mb-5 text-center">
                <div class="col-12" data-aos="fade-up">
                    <h6 class="text-accent font-weight-bold mb-3 tracking-widest uppercase">The Institute</h6>
                    <h2 class="section-title mb-4 text-white">Sankoree Institute of Global Negotiators</h2>
                    <p class="lead outfit max-w-700 mx-auto text-light-gray uppercase tracking-widest">In line with the Accra Reset Vision | The SIGN Mandate</p>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-4 mb-4" data-aos="fade-up" data-aos-delay="100">
                    <div class="feature-box p-4 border border-secondary rounded-lg h-100">
                        <h4 class="outfit font-weight-bold text-accent h5 mb-3">Sovereignty Studies</h4>
                        <p class="small lora text-light-gray">A new discipline that treats sovereignty as an essence emergent from building interlocking strategic capabilities. It is the capacity of a state to shape, not merely survive, the international order.</p>
                    </div>
                </div>
                <div class="col-lg-4 mb-4" data-aos="fade-up" data-aos-delay="200">
                    <div class="feature-box p-4 border border-secondary rounded-lg h-100">
                        <h4 class="outfit font-weight-bold text-accent h5 mb-3">Tactical Negotiation</h4>
                        <p class="small lora text-light-gray">Negotiation is something richer and deeper than mere technical skills. It is the tactical medium through which a state read and navigates the global architecture with agency.</p>
                    </div>
                </div>
                <div class="col-lg-4 mb-4" data-aos="fade-up" data-aos-delay="300">
                    <div class="feature-box p-4 border border-secondary rounded-lg h-100">
                        <h4 class="outfit font-weight-bold text-accent h5 mb-3">Accra Reset Vision</h4>
                        <p class="small lora text-light-gray">Operating under the Accra Reset banner, we Extract from the careers of iconic leaders the embedded wisdom of African statecraft for the next generation.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Event Detail Section: Sankoree Lectures -->
    <section id="lectures" class="lectures-section py-large overflow-hidden">
      <div class="container">
        <div class="row">
          <div class="col-lg-6 mb-5 mb-lg-0" data-aos="fade-right">
            <div class="image-box shadow-2xl overflow-hidden rounded-lg position-relative">
                <img src="../assets/images/about.jpg" class="img-fluid w-100" alt="Sankoree Lectures">
                <div class="overlay-accent"></div>
            </div>
          </div>
          <div class="col-lg-6 pl-lg-5" data-aos="fade-left">
            <h6 class="text-accent font-weight-bold mb-3 tracking-widest uppercase">Details: Event One</h6>
            <h2 class="section-title mb-4">INAUGURAL SANKOREE LECTURES</h2>
            <div class="brochure-text lora">
              <h4 class="h5 font-weight-bold mb-4 text-dark outfit tracking-normal">Bringing Sovereignty into Practice in Africa</h4>
              <p>The Inaugural Sankoree Lectures are the public birth of this field of studies and praxis. Convened on the virtual sidelines of the <strong>IMF and World Bank Spring Meetings</strong>—chosen as one of the most concentrated annual gatherings of global authority, and for the global South’s fraught history with such dealmaking—they will open the intellectual frontier and map its terrain.</p>
              <div class="accent-quote my-5">
                <p>Sovereignty is an essence emergent from building certain interlocking strategic capabilities. It is the capacity of a state to shape, not merely survive, the international order.</p>
              </div>
              <p>The lectures ask why the patient work of negotiating terms has not yet produced its own field of study, and what happens when it does. They introduce the practitioner-formation apparatus that the <strong>Sankoree Institute of Global Negotiators (SIGN)</strong> is building to populate it.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Event Detail Section: SIGN: Dealmakers 4 Africa -->
    <section id="dealmakers" class="obasanjo-section py-large bg-dark text-white overflow-hidden">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7 pr-lg-5 mb-5 mb-lg-0" data-aos="fade-up">
            <h6 class="text-accent font-weight-bold mb-3 tracking-widest uppercase">Details: Event Two</h6>
            <h2 class="section-title mb-4 text-white">SIGN: DEALMAKERS 4 AFRICA</h2>
            <div class="brochure-text lora text-light-gray">
              <p>Dealmakers 4 Africa is simultaneously a launch, a brainstorming, and a climax. It spotlights the experience of Africa’s leaders whose dealmaking capacity has led to a record of extraordinary reach and consequence.</p>
              <p>It launches the founding curricular text of the SIGN programme - the Sankoree Case Series exemplified by the first, <strong>A War Chest to Save Lives</strong> – into the realms of pedagogy.</p>
              <div class="impact-text my-5 outfit font-weight-bold">
                 EXTRACTING THE EMBEDDED WISDOM OF <span class="text-accent underline">AFRICAN STATECRAFT.</span>
              </div>
            </div>
          </div>
          <div class="col-lg-5" data-aos="zoom-in-left">
            <div class="stats-box p-5 border border-secondary rounded-lg bg-black">
                <div class="stat-item mb-4">
                    <span class="d-block display-4 font-weight-bold text-accent">OBASANJO</span>
                    <span class="small text-muted uppercase tracking-widest font-weight-bold">Event Host & Mentor</span>
                </div>
                <div class="stat-item">
                    <span class="d-block h3 font-weight-bold text-white mb-2 outfit uppercase">Washington DC</span>
                    <span class="small text-muted uppercase tracking-widest font-weight-bold">Event Venue</span>
                </div>
            </div>
          </div>
        </div>
      </div>
      <div class="background-text" data-aos="fade-in" data-aos-duration="2000">NEGOTIATION</div>
    </section>

    <!-- Call to Action -->
    <section id="contact" class="cta-section py-large text-center overflow-hidden">
        <div class="container" data-aos="zoom-in">
            <h2 class="display-4 font-weight-bold mb-4 outfit">Join the Vanguard</h2>
            <p class="lead mb-5 max-w-700 mx-auto lora">Become part of an initiative whose ambitions are as practical as they are intellectual. Shape and benefit from a mission at the frontier of African agency.</p>
            <router-link to="/SIGN/DC/2026" class="btn btn-primary btn-xl px-5 py-3 shadow-xl">REGISTER FOR SIGN DC 2026</router-link>
        </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import AppFooter from "@/components/AppFooter.vue";

export default {
  name: "BrochureIndex",
  components: {
    AppNavigation,
    AppFooter
  }
};
</script>

<style scoped>
.brochure-index {
  background-color: #fff;
  color: #1a1a1a;
  overflow-x: hidden;
}

.text-accent { color: #c40505; }
.bg-dark { background-color: #0d0d0d !important; }
.bg-black { background-color: #000 !important; }
.bg-light { background-color: #f8f9fa !important; }
.text-gray { color: #555; }
.text-light-gray { color: #bbb; }
.py-large { padding: 120px 0; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 4px; }
.tracking-wider { letter-spacing: 2px; }
.tracking-normal { letter-spacing: 0; }
.max-w-700 { max-width: 700px; }

.outfit { font-family: 'Outfit', sans-serif; }
.lora { font-family: 'Lora', serif; font-size: 1.15rem; line-height: 1.8; text-align: justify; }
.italic { font-style: italic; }

.hero-section {
  height: 100vh;
  min-height: 800px;
  background: radial-gradient(circle at 10% 20%, rgba(196, 5, 5, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 90% 80%, rgba(196, 5, 5, 0.08) 0%, transparent 50%);
  position: relative;
}

.eyebrow-text {
  font-family: 'Outfit', sans-serif;
  letter-spacing: 4px;
  font-weight: 700;
  color: #333;
}

.display-3 {
  font-family: 'Outfit', sans-serif;
  letter-spacing: -2px;
  line-height: 1;
}

.underline-effect {
  position: relative;
  display: inline-block;
}

.underline-effect::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(196, 5, 5, 0.15);
  z-index: -1;
}

.btn-primary { background-color: #c40505; border-color: #c40505; font-family: 'Outfit', sans-serif; font-weight: 700; }
.btn-primary:hover { background-color: #a00404; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(196, 5, 5, 0.2); }
.btn-outline-dark { border-width: 2px; font-family: 'Outfit', sans-serif; font-weight: 700; border-color: #333; color: #333; }
.btn-outline-dark:hover { background-color: #333; color: #fff; }

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column;
  align-items: center;
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 3px;
  opacity: 0.6;
}

.scroll-indicator .line {
  width: 2px;
  height: 40px;
  background: #c40505;
  margin-top: 15px;
  animation: scrollLine 2s infinite;
}

@keyframes scrollLine {
  0% { transform: scaleY(0); transform-origin: top; }
  50% { transform: scaleY(1); transform-origin: top; }
  51% { transform: scaleY(1); transform-origin: bottom; }
  100% { transform: scaleY(0); transform-origin: bottom; }
}

.section-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 800;
  font-size: 3rem;
  letter-spacing: -1px;
}

.event-table-wrapper {
    border: 1px solid rgba(0,0,0,0.05);
}

.table thead th {
    border: none;
    letter-spacing: 2px;
    font-size: 0.9rem;
}

.table tbody td {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
    border-color: rgba(0,0,0,0.05);
}

.accent-card {
    border-left: 8px solid #c40505;
    background: #fdfdfd;
    box-shadow: 10px 10px 0px rgba(196, 5, 5, 0.05);
}

.accent-quote {
  border-left: 5px solid #c40505;
  padding-left: 25px;
  font-weight: 700;
  font-size: 1.35rem;
  font-family: 'Outfit', sans-serif;
  color: #222;
  line-height: 1.4;
}

.impact-text {
  font-size: 1.4rem;
  letter-spacing: 1px;
  line-height: 1.5;
  color: #fff;
}

.overlay-accent {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(135deg, rgba(196, 5, 5, 0.15) 0%, transparent 60%);
}

.background-text {
  position: absolute;
  top: 50%;
  right: -50px;
  transform: translateY(-50%) rotate(90deg);
  font-size: 18rem;
  font-weight: 900;
  font-family: 'Outfit', sans-serif;
  color: rgba(255, 255, 255, 0.02);
  pointer-events: none;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .display-3 { font-size: 3.2rem; }
  .section-title { font-size: 2.2rem; }
  .py-large { padding: 70px 0; }
  .hero-section { height: auto; padding-top: 150px; padding-bottom: 80px; padding-left: 1.5rem; padding-right: 1.5rem; }
  .background-text { display: none; }
  .w-100-mobile { width: 100% !important; margin-right: 0 !important; }
}
</style>
